const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const RESOURCE_KINDS = [
  "browser-view",
  "candidate-worktree",
  "environment-process",
  "fiber",
  "listener",
  "measurement-root",
  "object-url",
  "ssh-forward",
  "subscription",
  "surface-lease",
  "terminal-attachment",
  "terminal-process",
  "timer",
  "websocket",
  "worker",
] as const;

export const RESOURCE_LIFETIMES = [
  "connection",
  "open-owner",
  "qualification-target",
  "retained-owner",
] as const;

export const RESOURCE_OWNER_KINDS = [
  "client-runtime",
  "environment",
  "qualification-target",
  "workspace-surface",
] as const;

export const RESOURCE_RELEASE_REASONS = [
  "close",
  "eviction",
  "failure",
  "interruption",
  "owner-disposal",
  "reconnect-settled",
  "replacement",
  "scope-removal",
] as const;

type DomainValue<Values extends readonly string[]> = Values[number];

export type ResourceKind = DomainValue<typeof RESOURCE_KINDS>;
export type ResourceLifetime = DomainValue<typeof RESOURCE_LIFETIMES>;
export type ResourceOwnerKind = DomainValue<typeof RESOURCE_OWNER_KINDS>;
export type ResourceReleaseReason = DomainValue<typeof RESOURCE_RELEASE_REASONS>;

export interface ResourceOwnerIdentity {
  readonly kind: ResourceOwnerKind;
  readonly identity: string;
}

export interface ResourceIdentity {
  readonly kind: ResourceKind;
  readonly identity: string;
}

export interface ResourceRegistration {
  readonly owner: ResourceOwnerIdentity;
  readonly resource: ResourceIdentity;
  readonly lifetime: ResourceLifetime;
}

export interface ResourceRelease {
  readonly owner: ResourceOwnerIdentity;
  readonly resource: ResourceIdentity;
  readonly reason: ResourceReleaseReason;
}

interface ReleasedResourceState {
  readonly state: "released";
  readonly reason: ResourceReleaseReason;
  readonly sequence: number;
}

interface ActiveResourceState {
  readonly state: "active";
}

export interface ResourceLedgerEntry extends ResourceRegistration {
  readonly registrationSequence: number;
  readonly release: ActiveResourceState | ReleasedResourceState;
}

export type RejectedResourceRelease =
  | (ResourceRelease & {
      readonly kind: "duplicate-release";
      readonly sequence: number;
      readonly firstReleaseSequence: number;
    })
  | (ResourceRelease & {
      readonly kind: "invalid-release";
      readonly sequence: number;
      readonly invalidReason: "unknown-resource" | "owner-mismatch";
    });

export interface ResourceLedgerEvidence {
  readonly activeCount: number;
  readonly resources: readonly ResourceLedgerEntry[];
  readonly rejectedReleases: readonly RejectedResourceRelease[];
}

export type ResourceRegistrationResult =
  | { readonly kind: "registered" }
  | { readonly kind: "duplicate-registration" };

export type ResourceReleaseResult =
  | { readonly kind: "released"; readonly releaseSequence: number }
  | { readonly kind: "duplicate-release"; readonly firstReleaseSequence: number }
  | {
      readonly kind: "invalid-release";
      readonly reason: "unknown-resource" | "owner-mismatch";
    };

export interface ResourceLedger {
  register(registration: ResourceRegistration): ResourceRegistrationResult;
  release(release: ResourceRelease): ResourceReleaseResult;
  snapshot(): ResourceLedgerEvidence;
}

const resourceKey = (resource: ResourceIdentity): string =>
  `${resource.kind}\u0000${resource.identity}`;

const sameOwner = (left: ResourceOwnerIdentity, right: ResourceOwnerIdentity): boolean =>
  left.kind === right.kind && left.identity === right.identity;

const copyOwner = (owner: ResourceOwnerIdentity): ResourceOwnerIdentity => ({ ...owner });
const copyResource = (resource: ResourceIdentity): ResourceIdentity => ({ ...resource });

export const createResourceLedger = (): ResourceLedger => {
  let sequence = 0;
  const resources = new Map<string, ResourceLedgerEntry>();
  const rejectedReleases: RejectedResourceRelease[] = [];

  return {
    register(registration) {
      sequence += 1;
      const key = resourceKey(registration.resource);
      if (resources.has(key)) return { kind: "duplicate-registration" };
      resources.set(key, {
        owner: copyOwner(registration.owner),
        resource: copyResource(registration.resource),
        lifetime: registration.lifetime,
        registrationSequence: sequence,
        release: { state: "active" },
      });
      return { kind: "registered" };
    },
    release(release) {
      sequence += 1;
      const recorded = resources.get(resourceKey(release.resource));
      if (recorded === undefined) {
        rejectedReleases.push({
          ...release,
          owner: copyOwner(release.owner),
          resource: copyResource(release.resource),
          kind: "invalid-release",
          sequence,
          invalidReason: "unknown-resource",
        });
        return { kind: "invalid-release", reason: "unknown-resource" };
      }
      if (!sameOwner(recorded.owner, release.owner)) {
        rejectedReleases.push({
          ...release,
          owner: copyOwner(release.owner),
          resource: copyResource(release.resource),
          kind: "invalid-release",
          sequence,
          invalidReason: "owner-mismatch",
        });
        return { kind: "invalid-release", reason: "owner-mismatch" };
      }
      if (recorded.release.state === "released") {
        rejectedReleases.push({
          ...release,
          owner: copyOwner(release.owner),
          resource: copyResource(release.resource),
          kind: "duplicate-release",
          sequence,
          firstReleaseSequence: recorded.release.sequence,
        });
        return {
          kind: "duplicate-release",
          firstReleaseSequence: recorded.release.sequence,
        };
      }
      resources.set(resourceKey(release.resource), {
        ...recorded,
        release: { state: "released", reason: release.reason, sequence },
      });
      return { kind: "released", releaseSequence: sequence };
    },
    snapshot() {
      const entries = [...resources.values()].map((entry) => ({
        ...entry,
        owner: copyOwner(entry.owner),
        resource: copyResource(entry.resource),
        release: { ...entry.release },
      }));
      return {
        activeCount: entries.filter((entry) => entry.release.state === "active").length,
        resources: entries,
        rejectedReleases: rejectedReleases.map((release) => ({
          ...release,
          owner: copyOwner(release.owner),
          resource: copyResource(release.resource),
        })),
      };
    },
  };
};

const isDomainValue = <Values extends readonly string[]>(
  values: Values,
  value: unknown,
): value is DomainValue<Values> =>
  typeof value === "string" && values.some((candidate) => candidate === value);

const isOwner = (value: unknown): value is ResourceOwnerIdentity =>
  isRecord(value) &&
  isDomainValue(RESOURCE_OWNER_KINDS, value.kind) &&
  typeof value.identity === "string" &&
  value.identity.length > 0;

const isResource = (value: unknown): value is ResourceIdentity =>
  isRecord(value) &&
  isDomainValue(RESOURCE_KINDS, value.kind) &&
  typeof value.identity === "string" &&
  value.identity.length > 0;

const isPositiveSafeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value > 0;

const isReleaseReason = (value: unknown): value is ResourceReleaseReason =>
  isDomainValue(RESOURCE_RELEASE_REASONS, value);

const isRejectedRelease = (value: unknown): value is RejectedResourceRelease =>
  isRecord(value) &&
  isOwner(value.owner) &&
  isResource(value.resource) &&
  isReleaseReason(value.reason) &&
  isPositiveSafeInteger(value.sequence) &&
  ((value.kind === "duplicate-release" && isPositiveSafeInteger(value.firstReleaseSequence)) ||
    (value.kind === "invalid-release" &&
      (value.invalidReason === "unknown-resource" || value.invalidReason === "owner-mismatch")));

const isLedgerEntry = (value: unknown): value is ResourceLedgerEntry =>
  isRecord(value) &&
  isOwner(value.owner) &&
  isResource(value.resource) &&
  isDomainValue(RESOURCE_LIFETIMES, value.lifetime) &&
  isPositiveSafeInteger(value.registrationSequence) &&
  isRecord(value.release) &&
  (value.release.state === "active" ||
    (value.release.state === "released" &&
      isReleaseReason(value.release.reason) &&
      isPositiveSafeInteger(value.release.sequence)));

const hasConsistentEvidence = (evidence: ResourceLedgerEvidence): boolean => {
  type EvidenceEvent =
    | {
        readonly kind: "registration";
        readonly sequence: number;
        readonly entry: ResourceLedgerEntry;
      }
    | {
        readonly kind: "release";
        readonly sequence: number;
        readonly entry: ResourceLedgerEntry;
      }
    | {
        readonly kind: "rejected-release";
        readonly sequence: number;
        readonly release: RejectedResourceRelease;
      };
  const resourceKeys = new Set<string>();
  const events: EvidenceEvent[] = [];
  let previousRegistrationSequence = 0;
  for (const entry of evidence.resources) {
    const key = resourceKey(entry.resource);
    if (resourceKeys.has(key) || entry.registrationSequence <= previousRegistrationSequence) {
      return false;
    }
    resourceKeys.add(key);
    previousRegistrationSequence = entry.registrationSequence;
    events.push({ kind: "registration", sequence: entry.registrationSequence, entry });
    if (entry.release.state === "released") {
      if (entry.release.sequence <= entry.registrationSequence) return false;
      events.push({ kind: "release", sequence: entry.release.sequence, entry });
    }
  }
  let previousRejectedReleaseSequence = 0;
  for (const release of evidence.rejectedReleases) {
    if (release.sequence <= previousRejectedReleaseSequence) return false;
    previousRejectedReleaseSequence = release.sequence;
    events.push({ kind: "rejected-release", sequence: release.sequence, release });
  }
  events.sort((left, right) => left.sequence - right.sequence);
  const resourceStates = new Map<
    string,
    { readonly owner: ResourceOwnerIdentity; releasedAt: number | undefined }
  >();
  let previousEventSequence = 0;
  for (const event of events) {
    if (event.sequence <= previousEventSequence) return false;
    previousEventSequence = event.sequence;
    if (event.kind === "registration") {
      resourceStates.set(resourceKey(event.entry.resource), {
        owner: event.entry.owner,
        releasedAt: undefined,
      });
      continue;
    }
    if (event.kind === "release") {
      const state = resourceStates.get(resourceKey(event.entry.resource));
      if (state === undefined || state.releasedAt !== undefined) return false;
      state.releasedAt = event.sequence;
      continue;
    }
    const state = resourceStates.get(resourceKey(event.release.resource));
    if (event.release.kind === "duplicate-release") {
      if (
        state === undefined ||
        !sameOwner(state.owner, event.release.owner) ||
        state.releasedAt !== event.release.firstReleaseSequence
      ) {
        return false;
      }
    } else if (
      (event.release.invalidReason === "unknown-resource" && state !== undefined) ||
      (event.release.invalidReason === "owner-mismatch" &&
        (state === undefined || sameOwner(state.owner, event.release.owner)))
    ) {
      return false;
    }
  }
  return (
    evidence.activeCount ===
    [...resourceStates.values()].filter((state) => !state.releasedAt).length
  );
};

export const isResourceLedgerEvidence = (value: unknown): value is ResourceLedgerEvidence => {
  if (
    !isRecord(value) ||
    typeof value.activeCount !== "number" ||
    !Number.isSafeInteger(value.activeCount) ||
    value.activeCount < 0 ||
    !Array.isArray(value.resources) ||
    !value.resources.every(isLedgerEntry) ||
    !Array.isArray(value.rejectedReleases) ||
    !value.rejectedReleases.every(isRejectedRelease)
  ) {
    return false;
  }
  return hasConsistentEvidence(value as unknown as ResourceLedgerEvidence);
};
